// The Timetable tab's selection maths: how many of each ticket a session can
// take from the bundle, quick select, the term sessions a term ticket brings
// with it, answers carried over from earlier bookings, and the bookings the
// selection becomes. A port of v1's BundleTimetable, pure.

import type {
  BundleBooking,
  BundleBookingOption,
  BundleTimetableSession,
  Question,
  UserFormAnswer,
} from "./types";
import { ticketCount } from "./bundleManage";

/** Tickets chosen: session id → ticket type id → how many. */
export type Selection = Record<string, Record<string, number>>;

/** A term ticket books every session of its term. */
export const TERM_TICKET_TYPES = ["full-term", "pro-rata-term", "bundle-term"];
/** These book the one session, so they're booked apart from term tickets. */
export const SESSION_SPECIFIC_TYPES = ["drop-in", "trial"];

export const guestsPerTicket = (option: BundleBookingOption) => option.numberOfGuestsPerTicket || 1;

const optionFor = (session: BundleTimetableSession | undefined, ticketTypeID: string) =>
  session?.bookingOptions?.find((o) => o.ticketTypeID === ticketTypeID);

/** Spots (guests) chosen on one session. */
export function sessionGuests(session: BundleTimetableSession, selection: Selection): number {
  const chosen = selection[session._id] ?? {};
  return Object.entries(chosen).reduce((sum, [id, qty]) => {
    const option = optionFor(session, id);
    return sum + qty * (option ? guestsPerTicket(option) : 1);
  }, 0);
}

/** Tickets chosen on one session. */
export const sessionTickets = (sessionId: string, selection: Selection) =>
  Object.values(selection[sessionId] ?? {}).reduce((sum, qty) => sum + qty, 0);

/** Spots chosen across the sessions shown. */
export function selectedGuests(sessions: BundleTimetableSession[], selection: Selection): number {
  return Object.keys(selection).reduce((sum, id) => {
    const session = sessions.find((s) => s._id === id);
    return session ? sum + sessionGuests(session, selection) : sum;
  }, 0);
}

/** Tickets chosen in all. */
export const selectedTickets = (selection: Selection) =>
  Object.keys(selection).reduce((sum, id) => sum + sessionTickets(id, selection), 0);

/** Tickets this customer already holds on a session through the bundle. */
export function bookedTickets(sessionId: string, upcoming: BundleBooking[]): number {
  return upcoming.filter((b) => b.session?._id === sessionId).reduce((sum, b) => sum + ticketCount(b), 0);
}

/** The most of a ticket a session could take from the bundle, before what's
 * chosen: the bundle's spots, the ticket's own limit, the session's places,
 * less what the customer already holds on it. */
export function optionCapacity(
  option: BundleBookingOption,
  session: BundleTimetableSession,
  remaining: number,
  alreadyBooked: number,
): number {
  if (!option.isEnabled) return 0;
  const per = guestsPerTicket(option);
  let capacity = Math.floor(remaining / per);
  if (option.maxLimit !== undefined && option.maxLimit > 0) {
    capacity = Math.min(capacity, option.maxLimit - (option.currentSold || 0));
  }
  if (session.availability !== undefined) {
    capacity = Math.min(capacity, Math.floor(session.availability / per));
  }
  return Math.max(0, capacity - alreadyBooked);
}

/** How many of a ticket can be chosen now, given the rest of the selection. */
export function maxSelectable(
  option: BundleBookingOption,
  session: BundleTimetableSession,
  sessions: BundleTimetableSession[],
  selection: Selection,
  remaining: number,
  alreadyBooked: number,
): number {
  const per = guestsPerTicket(option);
  const current = option.ticketTypeID ? (selection[session._id]?.[option.ticketTypeID] ?? 0) : 0;
  const spotsLeft = remaining - selectedGuests(sessions, selection) + current * per;
  return Math.min(optionCapacity(option, session, remaining, alreadyBooked), Math.floor(spotsLeft / per));
}

/** The selection with one ticket's count changed (0 removes it). */
export function setQuantity(selection: Selection, sessionId: string, ticketTypeID: string, quantity: number): Selection {
  const chosen = { ...(selection[sessionId] ?? {}) };
  if (quantity > 0) chosen[ticketTypeID] = quantity;
  else delete chosen[ticketTypeID];
  const next = { ...selection };
  if (Object.keys(chosen).length > 0) next[sessionId] = chosen;
  else delete next[sessionId];
  return next;
}

/** One ticket of the first bookable kind on each session, in order, while
 * the bundle has spots. */
export function quickSelect(sessions: BundleTimetableSession[], remaining: number, upcoming: BundleBooking[]): Selection {
  const selection: Selection = {};
  let guests = 0;
  for (const session of sessions) {
    if (guests >= remaining) break;
    const booked = bookedTickets(session._id, upcoming);
    const option = bundleOptions(session).find((o) => optionCapacity(o, session, remaining, booked) > 0);
    if (!option?.ticketTypeID) continue;
    const per = guestsPerTicket(option);
    if (remaining - guests >= per) {
      selection[session._id] = { [option.ticketTypeID]: 1 };
      guests += per;
    }
  }
  return selection;
}

export type AutoIncluded = { triggeredBy: string; ticketTypeID: string; ticketTypeName?: string };

const eventOf = (session: BundleTimetableSession) => session.eventId || session.event?._id || undefined;

/** Sessions a chosen term ticket brings with it (the same term's others). */
export function autoIncluded(sessions: BundleTimetableSession[], selection: Selection): Record<string, AutoIncluded> {
  const result: Record<string, AutoIncluded> = {};
  for (const [sessionId, chosen] of Object.entries(selection)) {
    const trigger = sessions.find((s) => s._id === sessionId);
    if (!trigger) continue;
    const eventId = eventOf(trigger);
    if (!eventId) continue;
    for (const [ticketTypeID, qty] of Object.entries(chosen)) {
      if (qty <= 0) continue;
      const option = optionFor(trigger, ticketTypeID);
      if (!option || !TERM_TICKET_TYPES.includes(option.type)) continue;
      for (const session of sessions) {
        if (session._id !== sessionId && eventOf(session) === eventId) {
          result[session._id] = { triggeredBy: sessionId, ticketTypeID, ticketTypeName: option.ticketTypeName };
        }
      }
    }
  }
  return result;
}

/** The tickets a session offers for bundle booking (less the term ticket
 * that already includes it). */
export function bundleOptions(session: BundleTimetableSession, included?: AutoIncluded): BundleBookingOption[] {
  return (session.bookingOptions ?? []).filter(
    (o) => o.isEnabled && o.allowBundleBooking !== false && !!o.ticketTypeID && o.ticketTypeID !== included?.ticketTypeID,
  );
}

/** The answers of the latest earlier booking that has any. */
export function previousAnswers(bookings: BundleBooking[]): UserFormAnswer[] {
  return bookings.find((b) => (b.userInputs?.length ?? 0) > 0)?.userInputs ?? [];
}

/** Answers for a session's questions, matched by label: answers given to
 * other sessions in this booking first, then earlier bookings'. */
export function prefillAnswers(questions: Question[] | undefined, current: UserFormAnswer[], previous: UserFormAnswer[]): UserFormAnswer[] {
  const pool = [...current, ...previous];
  if (!questions?.length || pool.length === 0) return [];
  const out: UserFormAnswer[] = [];
  questions.forEach((question, index) => {
    const match = pool.find((a) => a.label === question.title || (!!question.title && a.label?.includes(question.title)));
    if (match) out.push({ index, guestIndex: match.guestIndex ?? null, label: question.title, value: match.value });
  });
  return out;
}

export type SelectionEntry = {
  session: BundleTimetableSession;
  tickets: Array<{ ticketTypeID: string; quantity: number }>;
};

/** The bookings a selection becomes: per session, term-wide tickets in one
 * and drop-in/trial tickets in another. */
export function selectionEntries(sessions: BundleTimetableSession[], selection: Selection): SelectionEntry[] {
  const entries: SelectionEntry[] = [];
  for (const [sessionId, chosen] of Object.entries(selection)) {
    const session = sessions.find((s) => s._id === sessionId);
    if (!session) continue;
    const wide: SelectionEntry["tickets"] = [];
    const specific: SelectionEntry["tickets"] = [];
    for (const [ticketTypeID, quantity] of Object.entries(chosen)) {
      if (quantity <= 0) continue;
      const type = optionFor(session, ticketTypeID)?.type || "regular";
      (SESSION_SPECIFIC_TYPES.includes(type) ? specific : wide).push({ ticketTypeID, quantity });
    }
    if (wide.length) entries.push({ session, tickets: wide });
    if (specific.length) entries.push({ session, tickets: specific });
  }
  return entries;
}

export type TimetableQuery = {
  slug: string;
  courseIds: string[];
  page: number;
  limit: number;
  from?: string;
  to?: string;
};

/** The timetable request for the bundle's listings ("YYYY-MM-DD" bounds). */
export function timetableUrl(api: string, q: TimetableQuery): string {
  const params = new URLSearchParams({ page: String(q.page), limit: String(q.limit), availability: "available" });
  q.courseIds.forEach((id) => params.append("coursesIds[]", id));
  if (q.from) params.set("from", q.from);
  if (q.to) params.set("to", q.to);
  if (q.from || q.to) params.set("range", "custom");
  return `${api}/widget/pages/timetable/${encodeURIComponent(q.slug)}?${params.toString()}`;
}
